import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AdressePhysiqueBancaireModification } from '../../api/model/adresse-physique-bancaire-modification.model';
import { RequeteModifierCoordonneesBancaire } from '../../api/model/requete-modifier-coordonnees-bancaire.model';
import { FormulaireCoordonneesBancaires } from '../../models/formulaire/formulaire-coordonnees-bancaires.model';
import { FormulaireBancaireService } from '../formulaire-bancaire/formulaire-bancaire.service';
import { GlobalStoreService } from '../global-store/global-store.service';
import { ParticulierBancaire } from 'src/app/api/model/particulier-bancaire.model';
import { DateUtil } from 'src/app/util/date-util';
import { CompteSCGP } from 'src/app/api/model/compte-scgp.model';
import { CoordonneesUtil } from 'src/app/util/coordonnees-util';
import { TelephoneBancaire } from 'src/app/api/model/telephone-bancaire.model';
import { TelephoneTypeLieuEnum } from 'src/app/models/telephone-typelieu.enum';

@Injectable({
  providedIn: 'root',
})
export class RequeteCoordonneesBancaireService {
  dateUtil = new DateUtil();

  constructor(
    private readonly formulaireBancaireService: FormulaireBancaireService,
    private readonly globalStoreService: GlobalStoreService
  ) {}

  construireRequeteModifierCoordonnees(comptesSelectionnes: Array<CompteSCGP>): RequeteModifierCoordonneesBancaire {
    const form: FormGroup<FormulaireCoordonneesBancaires> = this.formulaireBancaireService.getForm();
    const courrielModifie = form?.value.courriel ?? '';
    const adresseModifie = this.construireAdresse(form);

    // Nettoie le code postal (enlève l'espace)
    if (adresseModifie?.codePostal) {
      adresseModifie.codePostal = adresseModifie.codePostal.replaceAll(' ', '');
    }

    const membre: ParticulierBancaire = this.globalStoreService.getValeurCoordonneesMembre();

    const empreinteCourriel = membre?.courriel?.empreinte ? membre.courriel.empreinte : null;
    const empreinteAdresse = membre?.adresseCourante?.empreinte ? membre.adresseCourante.empreinte : null;
    const empreintePostdatee = membre?.adressePostdatee?.empreinte ? membre.adressePostdatee.empreinte : null;

    if (adresseModifie) {
      adresseModifie.empreinte = empreinteAdresse;
      adresseModifie.empreintePostdatee = empreintePostdatee;
    }

    const payload: RequeteModifierCoordonneesBancaire = {
      courriel: {
        adresseCourriel: courrielModifie,
        empreinte: empreinteCourriel,
      },
      adresse: adresseModifie,
      comptesSCGP: comptesSelectionnes,
      telephones: this.construireTelephones(membre),
    };

    return payload;
  }

  construireRequeteSupprimmerPostdatee(): RequeteModifierCoordonneesBancaire {
    const membre: ParticulierBancaire = this.globalStoreService.getValeurCoordonneesMembre();
    const empreintePostdatee = membre?.adressePostdatee?.empreinte ? membre.adressePostdatee.empreinte : null;
    const comptesSCGP = membre?.comptesSCGP ? membre.comptesSCGP : [];

    return {
      adresse: { empreintePostdatee },
      comptesSCGP: comptesSCGP,
    };
  }

  private getCodeSubdivisionPays(form: FormGroup<FormulaireCoordonneesBancaires>): string | null {
    const pays = form.value.codePays;
    const subdivision = form.value.province;
    return subdivision ? `${pays}-${subdivision}` : null;
  }

  // Retourne null si la date est aujourd'hui
  private getDateDebutUtilisation(dateChangement: string): string | null {
    if (this.dateUtil.estMemeJour(dateChangement)) {
      return null;
    }
    return dateChangement;
  }

  private construireAdresse(
    form: FormGroup<FormulaireCoordonneesBancaires>
  ): AdressePhysiqueBancaireModification | null {
    if (form.value.utiliserMemeAdresse) {
      return null;
    }

    const codeSubdivisionPays = this.getCodeSubdivisionPays(form);

    // Nettoie le code postal (enlève l'espace)
    const codePostal = form.value.codePostal ? form.value.codePostal.replaceAll(' ', '') : null;

    const { numeroCivique, nomRue } = CoordonneesUtil.separerNumeroCiviqueEtNomRue(
      form.value.numeroCiviqueNomRue ?? ''
    );
    return {
      numeroCivique: numeroCivique ? numeroCivique : null,
      nomRue: nomRue ? nomRue : null,
      numeroUnite: form.value.appartement ? form.value.appartement : null,
      nomMunicipalite: form.value.ville ? form.value.ville : null,
      codeSubdivisionPays: codeSubdivisionPays,
      codePostal: codePostal,
      codePays: form.value.codePays ? form.value.codePays : null,
      dateDebutUtilisation: this.getDateDebutUtilisation(form.value.dateChangement),
    };
  }

  private construireTelephones(membre: ParticulierBancaire): Array<TelephoneBancaire> {
    const form = this.formulaireBancaireService.getForm();
    const telephones: TelephoneBancaire[] = [];

    const telephoneTypes = [
      { key: 'telephonePrincipal', typeLieu: TelephoneTypeLieuEnum.PRINCIPALE },
      { key: 'telephoneSecondaire', typeLieu: TelephoneTypeLieuEnum.SECONDAIRE },
      { key: 'telephoneTravail', typeLieu: TelephoneTypeLieuEnum.TRAVAIL },
    ] as const;

    telephoneTypes.forEach(({ key, typeLieu }) => {
      const telForm = form.value[key];
      if (!telForm) return;

      const empreinte = this.getEmpreinteTelephone(membre, typeLieu);

      // Nettoie le téléphone (enlève espaces et traits d'union)
      const telephone = telForm.telephone ? telForm.telephone.replace(/[\s-]/g, '') : null;

      if (!telForm.poste || telForm.poste.trim() === '') {
        telephones.push({
          numeroTelephone: telephone,
          typeAppareil: telForm.typeTelephone,
          typeLieu,
          empreinte,
        });
      } else {
        telephones.push({
          numeroTelephone: telephone,
          numeroPoste: telForm.poste,
          typeAppareil: telForm.typeTelephone,
          typeLieu,
          empreinte,
        });
      }
    });

    return telephones;
  }

  private getEmpreinteTelephone(membre: ParticulierBancaire, typeLieu: TelephoneTypeLieuEnum): string | undefined {
    if (!membre?.telephones) return undefined;

    if (typeLieu === TelephoneTypeLieuEnum.SECONDAIRE) {
      return membre.telephones
        .filter((t) => t.typeLieu === typeLieu)
        .sort((a, b) => (a.empreinte < b.empreinte ? 1 : -1))[0]?.empreinte;
    }

    return membre.telephones.find((t) => t.typeLieu === typeLieu)?.empreinte;
  }
}
